import { z } from 'zod';
export const CONDITION_RATINGS = ['C1', 'C2', 'C3', 'C4', 'C5', 'C6'];
export const conditionRatingSchema = z.enum(CONDITION_RATINGS);
export const CONDITION_DETAILS = {
    C1: { label: 'New / never occupied', defaultCents: 25000 },
    C2: { label: 'Fully renovated, like new', defaultCents: 17500 },
    C3: { label: 'Well kept, some updates', defaultCents: 10000 },
    C4: { label: 'Average, normal wear', defaultCents: 0 },
    C5: { label: 'Deferred maintenance', defaultCents: -10000 },
    C6: { label: 'Major repairs needed', defaultCents: -25000 },
};
export const UNIT_FEATURE_KEYS = [
    'remodeledKitchen',
    'stoneCounters',
    'stainlessAppliances',
    'dishwasher',
    'builtInMicrowave',
    'remodeledBath',
    'secondFullBath',
    'halfBath',
    'soakingTub',
    'walkInShower',
    'hardFloors',
    'centralAir',
    'wallAir',
    'dualPaneWindows',
    'walkInCloset',
    'fireplace',
    'furnished',
    'inUnitWasherDryer',
    'washerDryerHookups',
    'sharedLaundry',
    'topFloor',
    'cornerUnit',
    'view',
    'busyStreet',
    'privatePatio',
    'privateYard',
    'garage',
    'carport',
    'assignedParking',
    'streetParkingOnly',
    'gatedAccess',
    'pool',
    'extraStorage',
    'petsAllowed',
    'evCharging',
];
export const unitFeatureKeySchema = z.enum(UNIT_FEATURE_KEYS);
export const UNIT_FEATURE_DETAILS = {
    remodeledKitchen: { label: 'Remodeled kitchen', defaultCents: 7500 },
    stoneCounters: { label: 'Stone / quartz counters', defaultCents: 2500 },
    stainlessAppliances: { label: 'Stainless appliances', defaultCents: 2500 },
    dishwasher: { label: 'Dishwasher', defaultCents: 2500 },
    builtInMicrowave: { label: 'Built-in microwave', defaultCents: 500 },
    remodeledBath: { label: 'Remodeled bath', defaultCents: 5000 },
    secondFullBath: { label: 'Second full bath', defaultCents: 12500 },
    halfBath: { label: 'Half bath', defaultCents: 6000 },
    soakingTub: { label: 'Large / soaking tub', defaultCents: 2500 },
    walkInShower: { label: 'Walk-in shower', defaultCents: 2500 },
    hardFloors: { label: 'Hardwood / LVP floors', defaultCents: 3500 },
    centralAir: { label: 'Central A/C', defaultCents: 6000 },
    wallAir: { label: 'Wall / window A/C', defaultCents: 2000 },
    dualPaneWindows: { label: 'Dual-pane windows', defaultCents: 1500 },
    walkInCloset: { label: 'Walk-in closet', defaultCents: 1500 },
    fireplace: { label: 'Fireplace', defaultCents: 1500 },
    furnished: { label: 'Furnished', defaultCents: 30000 },
    inUnitWasherDryer: { label: 'Washer/dryer in unit', defaultCents: 10000 },
    washerDryerHookups: { label: 'W/D hookups only', defaultCents: 4000 },
    sharedLaundry: { label: 'Shared laundry on site', defaultCents: 1500 },
    topFloor: { label: 'Top floor', defaultCents: 1500 },
    cornerUnit: { label: 'Corner unit', defaultCents: 2000 },
    view: { label: 'View', defaultCents: 2500 },
    busyStreet: { label: 'Faces busy street / noise', defaultCents: -4000 },
    privatePatio: { label: 'Private patio / balcony', defaultCents: 4000 },
    privateYard: { label: 'Private yard', defaultCents: 6000 },
    garage: { label: 'Garage', defaultCents: 7500 },
    carport: { label: 'Covered / carport', defaultCents: 3500 },
    assignedParking: { label: 'Assigned uncovered', defaultCents: 1500 },
    streetParkingOnly: { label: 'Street parking only', defaultCents: -2500 },
    gatedAccess: { label: 'Gated / controlled access', defaultCents: 1500 },
    pool: { label: 'Pool', defaultCents: 2500 },
    extraStorage: { label: 'Extra storage', defaultCents: 1500 },
    petsAllowed: { label: 'Pets allowed', defaultCents: 2500 },
    evCharging: { label: 'EV charging', defaultCents: 2000 },
};
export const UNIT_FEATURE_GROUPS = [
    {
        key: 'kitchen',
        label: 'Kitchen',
        features: [
            'remodeledKitchen',
            'stoneCounters',
            'stainlessAppliances',
            'dishwasher',
            'builtInMicrowave',
        ],
    },
    {
        key: 'bath',
        label: 'Bath',
        hint: 'The bath count is in the unit type. Tick what sets this bath apart.',
        features: ['remodeledBath', 'secondFullBath', 'halfBath', 'soakingTub', 'walkInShower'],
    },
    {
        key: 'interior',
        label: 'Interior',
        features: [
            'hardFloors',
            'centralAir',
            'wallAir',
            'dualPaneWindows',
            'walkInCloset',
            'fireplace',
            'furnished',
        ],
    },
    {
        key: 'laundry',
        label: 'Laundry',
        features: ['inUnitWasherDryer', 'washerDryerHookups', 'sharedLaundry'],
    },
    {
        key: 'position',
        label: 'Position',
        features: ['topFloor', 'cornerUnit', 'view', 'busyStreet'],
    },
    { key: 'outdoor', label: 'Outdoor', features: ['privatePatio', 'privateYard'] },
    {
        key: 'parking',
        label: 'Parking',
        features: ['garage', 'carport', 'assignedParking', 'streetParkingOnly'],
    },
    {
        key: 'building',
        label: 'Building',
        features: ['gatedAccess', 'pool', 'extraStorage', 'petsAllowed', 'evCharging'],
    },
];
export const UTILITY_KEYS = ['waterSewerTrash', 'gas', 'electric'];
export const utilityKeySchema = z.enum(UTILITY_KEYS);
export const UTILITY_PAYERS = ['owner', 'tenant'];
export const utilityPayerSchema = z.enum(UTILITY_PAYERS);
export const UTILITY_PAYER_LABELS = {
    owner: 'Owner pays',
    tenant: 'Tenant pays',
};
export const UTILITY_DETAILS = {
    waterSewerTrash: { label: 'Water / sewer / trash', defaultCents: 6000 },
    gas: { label: 'Gas (heat / cooking)', defaultCents: 3000 },
    electric: { label: 'Electric', defaultCents: 8000 },
};
export const adjustmentKeySchema = z.enum([
    ...CONDITION_RATINGS,
    ...UNIT_FEATURE_KEYS,
    ...UTILITY_KEYS,
]);
export const renovatedYearSchema = z.number().int().min(1800).max(2200);
export const unitFeaturesSchema = z.record(z.string(), z.literal(true));
export const unitUtilitiesSchema = z.record(z.string(), utilityPayerSchema);
export const ADJUSTMENT_VALUE_CENTS_RANGE = { min: -1_000_000, max: 1_000_000 };
export const adjustmentValuesSchema = z.record(z.string(), z.number().int());
export const suggestibleAdjustmentKeySchema = adjustmentKeySchema.exclude(['C4']);
export const SUGGESTIBLE_ADJUSTMENT_KEYS = suggestibleAdjustmentKeySchema.options;
export const SUGGESTIBLE_ADJUSTMENT_ITEMS = [
    ...CONDITION_RATINGS.flatMap((rating) => {
        const key = suggestibleAdjustmentKeySchema.safeParse(rating);
        return key.success
            ? [{ key: key.data, label: CONDITION_DETAILS[rating].label, group: 'Condition' }]
            : [];
    }),
    ...UNIT_FEATURE_GROUPS.flatMap((group) => group.features.map((feature) => ({
        key: feature,
        label: UNIT_FEATURE_DETAILS[feature].label,
        group: group.label,
    }))),
    ...UTILITY_KEYS.map((key) => ({
        key,
        label: UTILITY_DETAILS[key].label,
        group: 'Utilities',
    })),
];
const SUGGESTION_BASIS_MAX_LENGTH = 300;
export const adjustmentSuggestionAnswerSchema = z.object({
    values: z
        .array(z.object({
        key: suggestibleAdjustmentKeySchema,
        monthlyDollars: z
            .number()
            .int()
            .min(ADJUSTMENT_VALUE_CENTS_RANGE.min / 100)
            .max(ADJUSTMENT_VALUE_CENTS_RANGE.max / 100),
        basis: z.string().max(SUGGESTION_BASIS_MAX_LENGTH),
    }))
        .refine((values) => new Set(values.map((value) => value.key)).size === values.length, {
        message: 'each key may appear at most once',
    }),
});
export const adjustmentSuggestionEntrySchema = z.object({
    cents: z.number().int(),
    basis: z.string(),
});
export const adjustmentSuggestionValuesSchema = z.record(z.string(), adjustmentSuggestionEntrySchema);
export const adjustmentValueOriginsSchema = z.record(z.string(), z.object({ suggestionId: z.number().int(), cents: z.number().int() }));
export function adjustmentValueSource(values, origins, key) {
    const stored = Object.hasOwn(values, key) ? values[key] : undefined;
    if (stored === undefined)
        return { kind: 'placeholder' };
    const origin = Object.hasOwn(origins, key) ? origins[key] : undefined;
    if (origin !== undefined && origin.cents === stored) {
        return { kind: 'ai', suggestionId: origin.suggestionId };
    }
    return { kind: 'hand' };
}
//# sourceMappingURL=rentRadarFeatures.js.map